// src/router/RouteGuard.tsx
import { useEffect, ReactNode, FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadThemeFromServer } from '@/utils/themeSync';
import { useUserStore } from '../store/useUserStore';
import { PATHS } from './paths';
import { getSafeRedirectPath, isAuthPath } from './redirect';

interface RouteGuardProps {
  children: ReactNode;
}

const RouteGuard: FC<RouteGuardProps> = ({ children }) => {
  const { token, user } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 如果用户已登录且有 preferences，同步主题
    if (token && user?.preferences?.theme) {
      loadThemeFromServer(user.preferences.theme);
    }

    const isPublic = isAuthPath(location.pathname) || location.pathname === PATHS.subscriptionCancel;
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get('from');

    // 排除 googleCallback 路径，让它自己处理跳转逻辑（需要先检查 is_first_login）
    if (token && isAuthPath(location.pathname) && location.pathname !== PATHS.googleCallback) {
      const redirectTo = getSafeRedirectPath(from);
      if (redirectTo !== location.pathname) {
        navigate(redirectTo, { replace: true });
      }
      return;
    }

    // 如果不是公开路径且用户未登录，跳转到登录页
    if (!isPublic && !token) {
      // 保存当前路径，登录后跳转回来
      const from = encodeURIComponent(location.pathname + location.search);
      navigate(`${PATHS.login}?from=${from}`, { replace: true });
    }
  }, [location.pathname, location.search, token, user, navigate]);

  return <>{children}</>;
};

export default RouteGuard;
